import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { reviewApi } from '../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import LanguageFlag from './LanguageFlag';
import { BrainCircuit, ArrowRight, Flame } from 'lucide-react';

const DISMISS_KEY = 'iling_review_popup_dismissed_date';
const SESSION_SHOWN_KEY = 'iling_review_popup_shown_session';

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function ReviewDuePopup() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [streak, setStreak] = useState(null);
  const [dontShow, setDontShow] = useState(false);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (loading || !user) return;
    if (checkedRef.current) return;

    // Skip if we already evaluated in this browser session
    try {
      if (sessionStorage.getItem(SESSION_SHOWN_KEY) === '1') {
        checkedRef.current = true;
        return;
      }
    } catch (e) {
      // sessionStorage may be unavailable
    }

    // Skip if the user dismissed for today
    try {
      if (localStorage.getItem(DISMISS_KEY) === todayStr()) {
        checkedRef.current = true;
        return;
      }
    } catch (e) {
      // ignore
    }

    // Skip on onboarding page and oracle embed
    if (location.pathname === '/onboarding' || location.pathname === '/oracle-embed') return;

    checkedRef.current = true;

    (async () => {
      try {
        // Fetch streak + summary in parallel. Streak is optional (if fetch
        // fails we simply don't render the nudge).
        const [summaryRes, streakRes] = await Promise.allSettled([
          reviewApi.getQueueSummary(),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/goals/streak`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null),
        ]);
        const data = summaryRes.status === 'fulfilled' ? (summaryRes.value?.data || {}) : {};
        const streakData = streakRes.status === 'fulfilled' ? streakRes.value : null;
        try {
          sessionStorage.setItem(SESSION_SHOWN_KEY, '1');
        } catch (e) {
          // ignore
        }
        if ((data.total || 0) > 0 && Array.isArray(data.breakdown) && data.breakdown.length) {
          setSummary(data);
          if (streakData && (streakData.current_streak || 0) > 0) {
            setStreak(streakData);
          }
          setOpen(true);
        }
      } catch (err) {
        // Fail silently — this is an ambient reminder, not a critical flow
        console.warn('[ReviewDuePopup] summary fetch failed', err?.message || err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  const handleClose = (nextOpen) => {
    setOpen(nextOpen);
    if (!nextOpen && dontShow) {
      try {
        localStorage.setItem(DISMISS_KEY, todayStr());
      } catch (e) {
        // ignore
      }
    }
  };

  const handleStartReview = (languageId) => {
    if (dontShow) {
      try {
        localStorage.setItem(DISMISS_KEY, todayStr());
      } catch (e) {
        // ignore
      }
    }
    setOpen(false);
    const target = languageId ? `/review?language_id=${encodeURIComponent(languageId)}` : '/review';
    navigate(target);
  };

  if (!summary) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="review-due-popup"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              <BrainCircuit className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <DialogTitle data-testid="review-due-popup-title">
                {summary.total} {summary.total === 1 ? 'review' : 'reviews'} due
              </DialogTitle>
              <DialogDescription>
                Keep your vocabulary strong — pick a language to get started.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {streak && (streak.current_streak || 0) > 0 && (
          <div
            className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300"
            data-testid="review-due-streak-banner"
          >
            <Flame className="h-4 w-4 shrink-0" />
            <span>
              <span className="font-semibold tabular-nums">{streak.current_streak}-day streak</span>
              {streak.current_streak >= 3 ? ' — don\'t let it slip!' : ' — keep it going!'}
            </span>
          </div>
        )}

        <ul className="divide-y divide-border rounded-md border max-h-60 overflow-y-auto">
          {summary.breakdown.map((row) => (
            <li
              key={row.language_id || row.code || row.name}
              className="flex items-center justify-between gap-3 px-3 py-2.5"
              data-testid={`review-due-row-${row.code || row.language_id}`}
            >
              <div className="flex min-w-0 items-center gap-2.5">
                {row.code ? <LanguageFlag code={row.code} size="md" /> : null}
                <span className="truncate text-sm font-medium">{row.name}</span>
                <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums text-muted-foreground">
                  {row.count}
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleStartReview(row.language_id)}
                data-testid={`review-due-start-${row.code || row.language_id}`}
              >
                Start
                <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>

        <div className="flex items-center justify-between gap-3 pt-1">
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
            <Checkbox
              checked={dontShow}
              onCheckedChange={(v) => setDontShow(!!v)}
              data-testid="review-due-dont-show-today"
            />
            Don't show this again today
          </label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleClose(false)}
            data-testid="review-due-dismiss"
          >
            Later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
